import { Controller } from "stimulus"
import { fetchBreakdown, renderBreakdownTable, syncBreakdownFrame } from "../lib/aia_breakdowns"

export default class extends Controller {
  static targets = [
    "progressLabel",
    "sidebar",
    "panel",
    "chatPortfolio",
    "panelRoot",
    "portfolioSelect",
    "assetAllocationFrame",
    "equityCountryFrame",
    "equityCapitalisationFrame",
    "currenciesFrame"
  ]

  connect() {
    this.loadedBreakdowns = new Set()
    this.canonicalizeUrl()
  }

  canonicalizeUrl() {
    const canonicalUrl = this.data.get("canonical-url")
    if (canonicalUrl && `${window.location.pathname}${window.location.search}` !== canonicalUrl) {
      window.history.replaceState({}, "", canonicalUrl)
    }
  }

  switchPortfolio(event) {
    this.loadPortfolioPanel(event.target.value)
  }

  loadPortfolioPanel(portfolioId) {
    fetch(`${this.data.get("panel-url")}&portfolio_id=${encodeURIComponent(portfolioId)}`, {
      method: "GET",
      headers: { "Accept": "application/json" }
    }).then((response) => {
      if (!response.ok) throw new Error("portfolio_panel_error")
      return response.json()
    }).then((data) => {
      this.renderPanelPayload(data)
    }).catch(() => {
      this.panelTarget.innerHTML = "<div class=\"aia-empty-state\">Impossible de charger ce portefeuille.</div>"
    })
  }

  renderPanelPayload(data) {
    if (data.panel_html) this.panelTarget.innerHTML = data.panel_html
    if (data.sidebar_html && this.hasSidebarTarget) this.sidebarTarget.innerHTML = data.sidebar_html
    if (data.portfolio_name && this.hasChatPortfolioTarget) this.chatPortfolioTarget.textContent = data.portfolio_name
    this.updateProgress(data.progress, data.progress_complete)
    this.loadedBreakdowns = new Set()
  }

  loadBreakdownTab(event) {
    const kind = event.currentTarget.dataset.breakdownKind
    const config = this.breakdownConfigs().find((candidate) => candidate.kind === kind)
    if (!config || !config.frame || !config.url || this.loadedBreakdowns.has(kind)) return

    this.loadedBreakdowns.add(kind)
    const inventoryUrl = `${config.inventoryUrl}&kind=${encodeURIComponent(kind)}`

    Promise.all([fetchBreakdown(config.url), fetchBreakdown(inventoryUrl)]).then(([data, entries]) => {
      renderBreakdownTable(config.frame, config, data)
      syncBreakdownFrame(config.frame, config, entries)
    }).catch(() => {
      this.loadedBreakdowns.delete(kind)
      config.frame.innerHTML = `<div class="aia-empty-state">${config.errorMessage}</div>`
    })
  }

  breakdownConfigs() {
    if (!this.hasPanelRootTarget) return []

    const root = this.panelRootTarget

    return [
      {
        kind: "asset",
        dataKey: "asset",
        url: root.dataset.assetAllocationUrl,
        inventoryUrl: root.dataset.analyticalInventoryUrl,
        frame: this.hasAssetAllocationFrameTarget ? this.assetAllocationFrameTarget : null,
        title: "Classe d'actifs",
        emptyMessage: "Aucune allocation d'actifs disponible pour ce portefeuille.",
        errorMessage: "Impossible de charger la répartition par allocation d'actifs."
      },
      {
        kind: "equity_country",
        dataKey: "equity_country",
        url: root.dataset.equityCountryUrl,
        inventoryUrl: root.dataset.analyticalInventoryUrl,
        frame: this.hasEquityCountryFrameTarget ? this.equityCountryFrameTarget : null,
        title: "Zone géographique",
        assetMultiplier: "equities",
        consolidateCountries: true,
        emptyMessage: "Aucune répartition géographique actions disponible pour ce portefeuille.",
        errorMessage: "Impossible de charger la répartition géographique actions."
      },
      {
        kind: "equity_capitalisation",
        dataKey: "equity_capitalisation",
        url: root.dataset.equityCapitalisationUrl,
        inventoryUrl: root.dataset.analyticalInventoryUrl,
        frame: this.hasEquityCapitalisationFrameTarget ? this.equityCapitalisationFrameTarget : null,
        title: "Taille de capitalisation",
        emptyMessage: "Aucune répartition par tailles de capitalisation disponible pour ce portefeuille.",
        errorMessage: "Impossible de charger la répartition par tailles de capitalisation."
      },
      {
        kind: "currencies",
        dataKey: "currencies",
        url: root.dataset.currenciesUrl,
        inventoryUrl: root.dataset.analyticalInventoryUrl,
        frame: this.hasCurrenciesFrameTarget ? this.currenciesFrameTarget : null,
        title: "Devise",
        emptyMessage: "Aucune répartition par devises disponible pour ce portefeuille.",
        errorMessage: "Impossible de charger la répartition par devises."
      }
    ]
  }

  itemEvent(event) {
    const button = event.currentTarget
    this.patchItem({ item_id: button.dataset.itemId, event: button.dataset.event })
  }

  applyInventoryTarget(event) {
    const input = event.currentTarget
    const row = input.closest("[data-share-id]")
    if (!row || !this.hasPanelRootTarget) return

    fetch(this.data.get("inventory-url"), {
      method: "PATCH",
      headers: this.jsonHeaders(),
      body: JSON.stringify({
        portfolio_id: this.panelRootTarget.dataset.portfolioId,
        share_id: row.dataset.shareId,
        target_weight: input.value
      })
    }).then((response) => {
      if (!response.ok) return response.json().then((data) => Promise.reject(data))
      return response.json()
    }).then((data) => {
      this.renderPanelPayload(data)
    }).catch((error) => {
      if (error && error.error) window.alert(error.error)
      this.loadPortfolioPanel(this.panelRootTarget.dataset.portfolioId)
    })
  }

  patchItem(payload) {
    fetch(this.data.get("item-url"), {
      method: "PATCH",
      headers: this.jsonHeaders(),
      body: JSON.stringify(payload)
    }).then((response) => {
      if (!response.ok) throw new Error("item_error")
      return response.json()
    }).then((data) => {
      this.renderPanelPayload(data)
    }).catch(() => {})
  }

  portfolioEvent(event) {
    const button = event.currentTarget

    fetch(this.data.get("portfolio-url"), {
      method: "PATCH",
      headers: this.jsonHeaders(),
      body: JSON.stringify({ portfolio_id: button.dataset.portfolioId, event: button.dataset.event })
    }).then((response) => {
      if (!response.ok) return response.json().then((data) => Promise.reject(data))
      return response.json()
    }).then((data) => {
      if (data.next_portfolio_id) {
        this.loadPortfolioPanel(data.next_portfolio_id)
        this.updateProgress(data.progress, data.progress_complete)
      } else {
        this.renderPanelPayload(data)
      }
    }).catch((error) => {
      if (error && error.panel_html) this.renderPanelPayload(error)
      if (error && error.error) window.alert(error.error)
    })
  }

  rebalance() {
    if (!this.hasPanelRootTarget) return

    fetch(this.data.get("rebalance-url"), {
      method: "POST",
      headers: this.jsonHeaders(),
      body: JSON.stringify({ portfolio_id: this.panelRootTarget.dataset.portfolioId })
    }).then((response) => {
      if (!response.ok) return response.json().then((data) => Promise.reject(data))
      return response.json()
    }).then((data) => {
      this.renderPanelPayload(data)
    }).catch((error) => {
      if (error && error.error) window.alert(error.error)
    })
  }

  submitAll() {
    fetch(this.data.get("submit-url"), {
      method: "POST",
      headers: this.jsonHeaders()
    }).then((response) => {
      if (!response.ok) return response.json().then((data) => Promise.reject(data))
      return response.json()
    }).then((data) => {
      if (data.redirect_url) window.location.href = data.redirect_url
    }).catch((error) => {
      if (error && error.error) window.alert(error.error)
    })
  }

  updateProgress(label, complete) {
    if (!this.hasProgressLabelTarget || !label) return

    this.progressLabelTarget.textContent = label
    this.progressLabelTarget.classList.toggle("is-complete", complete === true)
    this.progressLabelTarget.classList.toggle("is-pending", complete !== true)
  }

  jsonHeaders() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
    }
  }

}
